import { z } from 'zod';

/**
 * Asana, as the app sees it (PRD 51).
 *
 * ⚠ A `gid` is Asana's id and is a **string of digits**, not a number. It
 * exceeds what a JavaScript number holds exactly, and one rounded gid is a
 * task filed into a project nobody chose.
 */
const gid = z.string().min(1).max(64);

export const asanaRef = z.object({ gid, name: z.string() });
export type AsanaRefDto = z.infer<typeof asanaRef>;

export const asanaStatus = z.object({
  connected: z.boolean(),
  /** Whose Asana it is, so somebody can see they connected the right account. */
  name: z.string().nullable(),
  /** Where the picker starts. */
  defaultWorkspaceGid: gid.nullable(),
  /** Whether an OAuth button can be offered at all, or only a pasted token. */
  oauthAvailable: z.boolean(),
});
export type AsanaStatus = z.infer<typeof asanaStatus>;

/**
 * A personal access token, pasted.
 *
 * The path that needs no app registration and no administrator — which is the
 * case this integration was asked for in: *"I am not admin in TraceAir asana
 * and can't add new apps"*.
 */
export const connectAsanaBody = z.object({ token: z.string().trim().min(8).max(400) });
export type ConnectAsanaBody = z.infer<typeof connectAsanaBody>;

export const convertToAsanaBody = z.object({
  workspaceGid: gid,
  /** Optional: a task with no project lands in the person's My Tasks. */
  projectGid: gid.nullable().optional(),
});
export type ConvertToAsanaBody = z.infer<typeof convertToAsanaBody>;

export const convertedTask = z.object({
  url: z.string(),
  /**
   * The owner Asana would not take as the assignee — the task was created
   * unassigned, and whoever pressed is told to assign it there. Null when it
   * was assigned, or when nobody owned it.
   */
  unassigned: z.string().nullable(),
});
export type ConvertedTask = z.infer<typeof convertedTask>;
